/**
 * `computeReviewScore` — the single most important function in the Performance Reviews module. It
 * is the number that decides someone's raise, so it is PURE (no I/O, no Prisma, no Date.now()) and
 * exact: every input and output is a `Decimal` (decimal.js), never a JS float, matching CLAUDE.md
 * invariant 2 exactly as `packages/payroll`'s statutory engine does for money.
 *
 * WHY THE ROUTE MUST NEVER REIMPLEMENT THIS INLINE: an integration test asserts the route's
 * persisted `overallScore` equals a direct call to this function on the same inputs — see
 * `docs/plans/performance-management/performance-reviews-plan.md`'s Scoring section. A hand-rolled
 * weighted average in the route that happens to agree today is exactly the kind of divergence that
 * shows up as a silent payroll-adjacent bug six months later.
 *
 * UNITS, the part most likely to be gotten wrong:
 *   - `competencyScore` is a weighted mean of `ratings[].rating`, on the SAME scale the ratings are
 *     given on (1..`ratingScaleMax`, default 5) — e.g. "4.20".
 *   - `goalScore` is a weighted mean of `goalLinks[].achievedPercent` (or its
 *     `managerAdjustedPercent` override, when present — see `ReviewGoalLink`'s header on why the
 *     override, not the raw achieved value, must win once a manager has adjusted it), as a
 *     PERCENTAGE 0-100 — e.g. "78.00". Each goal's achievement is capped at 100 first (#19: 150%
 *     scores as 100%). This is the number the UI's "Goal Achievement" ring shows.
 *
 * THE AUTHORITATIVE REVIEW SCORE IS `computeManagerLedScore` (bottom of this file — D1, weight
 * transfer between halves). `computeReviewScore` remains for read-side callers that blend whatever
 * ratings they are handed.
 *   - `overallScore` blends the two on the RATING scale, not the percentage one: `goalScore` is
 *     rescaled to `0..ratingScaleMax` before the `goalWeightPercent`/`competencyWeightPercent`
 *     blend, because `overallScore` is what the workflow's outlier condition compares against 4.5/
 *     2.0 (1..5 territory) and what a human reads next to `finalRating` (also 1..5). Blending a
 *     0-100 number against a 1-5 one without rescaling would silently make goals worth roughly 20x
 *     what the configured weight says.
 *
 * WEIGHTING, within each half: every `weightPercent` is normalized against the SUM of the weights
 * actually present, not assumed to already total 100. A review with one competency still submitted
 * (others not yet rated) must not silently score as if the unrated ones were zero — see the plan's
 * boundary-case tests. An input array with a zero total weight (empty, or every weight literally 0)
 * scores that half as "0.00" rather than dividing by zero.
 */

import { Decimal } from 'decimal.js';
import { DEFAULT_RATING_SCALE_MAX, defaultRatingBands } from '../constants/performance.js';

/** One `ReviewCompetencyRating` row's contribution — deliberately just the two fields the maths
 * needs, not the whole Prisma row, so the caller decides (self vs manager vs already-calibrated
 * value) which rating field feeds the scorer. */
export interface ScoreRatingInput {
  /** `ReviewTemplateCompetency.weightPercent`, snapshotted onto the rating row — a decimal string. */
  weightPercent: string;
  /** The rating actually being scored — 1..ratingScaleMax, one decimal place — as a decimal string. */
  rating: string;
}

/** One `ReviewGoalLink` row's contribution. `managerAdjustedPercent`, when set, wins over
 * `achievedPercent` — see `ReviewGoalLink`'s header comment in the plan's Data model section. */
export interface ScoreGoalLinkInput {
  weightPercent: string;
  achievedPercent: string;
  managerAdjustedPercent?: string | null;
}

/**
 * A `ReviewCycle.ratingBands` entry, reused here to LABEL a score, not just to target a
 * calibration distribution — `bands` sorted by `minScore` ascending; a score falls into the last
 * band whose `minScore` it meets or exceeds. Optional: with no bands supplied, a default 5-way
 * split of `1..ratingScaleMax` is used (the mockup's "1 Dev / 2 Prof / 3 Meets / 4 Exceeds /
 * 5 Outst" labels), scaled proportionally for a non-5 `ratingScaleMax`.
 */
export interface RatingBand {
  band: string;
  minScore: string;
}

export interface ComputeReviewScoreInput {
  ratings: ScoreRatingInput[];
  goalLinks: ScoreGoalLinkInput[];
  /** `ReviewCycle.goalWeightPercent` — decimal string, e.g. "60.00". `goalWeightPercent` +
   * `competencyWeightPercent` should sum to 100; this function does not enforce that (the route's
   * `ReviewCycle` validation does) — it simply divides by 100, so a cycle that somehow saved 60/50
   * would over-weight both halves rather than throwing here. */
  goalWeightPercent: string;
  competencyWeightPercent: string;
  /** Defaults to 5 — `ReviewCycle.ratingScaleMax`. */
  ratingScaleMax?: number;
  ratingBands?: RatingBand[];
}

export interface ComputeReviewScoreResult {
  /** Weighted mean of ratings, on the 1..ratingScaleMax scale. "0.00" when `ratings` is empty or
   * every weight is 0. */
  competencyScore: string;
  /** Weighted mean of goal achievement, 0-100. "0.00" when `goalLinks` is empty or every weight is
   * 0. */
  goalScore: string;
  /** The blended score that decides a raise, on the 1..ratingScaleMax scale — see this file's
   * header for the rescaling this performs. */
  overallScore: string;
  /** `null` when `overallScore` is "0.00" with nothing to label (both halves empty) — every other
   * value always falls in exactly one band. */
  ratingLabel: string | null;
}

function defaultBands(ratingScaleMax: number): RatingBand[] {
  return defaultRatingBands(ratingScaleMax);
}

const ONE_HUNDRED = new Decimal(100);

/** #19: goal achievement counts up to 100% and no further. `null` (never recorded) scores as 0. */
function capAchievement(percent: string | null | undefined): Decimal {
  if (percent === null || percent === undefined) return new Decimal(0);
  return Decimal.min(ONE_HUNDRED, new Decimal(percent));
}

/** Weighted mean of `values[i].value` by `values[i].weight`, normalized against the sum of the
 * weights present (never assumed to total 100) — Decimal throughout. Returns `new Decimal(0)` when
 * the array is empty or every weight is zero, rather than dividing by zero. */
function weightedMean(values: Array<{ weight: Decimal; value: Decimal }>): Decimal {
  const totalWeight = values.reduce((sum, v) => sum.plus(v.weight), new Decimal(0));
  if (totalWeight.isZero()) return new Decimal(0);
  const weightedSum = values.reduce((sum, v) => sum.plus(v.value.times(v.weight)), new Decimal(0));
  return weightedSum.div(totalWeight);
}

/**
 * PRF-31's "one rating label scheme everywhere": the single place that decides which bands apply
 * to a cycle — `scoreBands` when the cycle configured its own, else the standard split for
 * `ratingScaleMax` (same fallback `computeReviewScore` uses via `defaultBands`) — sorted ascending
 * by `minScore` so every caller (a SQL `CASE` builder included) can rely on the order without
 * re-deriving it. `ratingBandFor`/`computeReviewScore` use this internally; a caller that needs to
 * label many rows in bulk (the SQL band-bucketing in `performance-insights.ts` and
 * `review-cycle-rollup.ts`, and any future calibration/mobile consumer) should call this directly
 * rather than re-implement "empty means default" itself.
 */
export function resolveRatingBands(
  bands: RatingBand[],
  ratingScaleMax: number = DEFAULT_RATING_SCALE_MAX,
): RatingBand[] {
  const resolved = bands.length > 0 ? bands : defaultBands(ratingScaleMax);
  return [...resolved].sort((a, b) => new Decimal(a.minScore).comparedTo(b.minScore));
}

/**
 * Which `RatingBand` a score falls into — exported for callers that need to label a score outside
 * a full `computeReviewScore` call (merit-cycle-trigger.ts's per-recommendation `ratingBand`, one
 * example). Accepts a decimal string, same convention as every other cross-module score value
 * (invariant 2); `bands` defaults to the standard 5-way split for `ratingScaleMax` when the cycle
 * has not configured its own (same fallback `computeReviewScore` itself uses via `defaultBands`).
 */
export function ratingBandFor(
  score: string,
  bands: RatingBand[],
  ratingScaleMax: number = DEFAULT_RATING_SCALE_MAX,
): string | null {
  return labelFor(new Decimal(score), resolveRatingBands(bands, ratingScaleMax));
}

function labelFor(score: Decimal, bands: RatingBand[]): string | null {
  if (bands.length === 0) return null;
  const sorted = [...bands].sort((a, b) => new Decimal(a.minScore).comparedTo(b.minScore));
  let match: RatingBand | null = null;
  for (const band of sorted) {
    if (score.greaterThanOrEqualTo(band.minScore)) {
      match = band;
    }
  }
  // Below every band's floor (e.g. score 0 with bands starting at 1, the "nothing rated yet" case)
  // — falls back to the lowest band rather than returning null, since a "0.00" from an empty input
  // is still a real state a UI must label something (the lowest, not a crash). `sorted[0]` is safe:
  // this function returned early above when `bands` (and therefore `sorted`) is empty.
  const lowest = sorted[0];
  if (!lowest) return null;
  return (match ?? lowest).band;
}

export function computeReviewScore(input: ComputeReviewScoreInput): ComputeReviewScoreResult {
  const ratingScaleMax = input.ratingScaleMax ?? DEFAULT_RATING_SCALE_MAX;

  const competencyScore = weightedMean(
    input.ratings.map((r) => ({
      weight: new Decimal(r.weightPercent),
      value: new Decimal(r.rating),
    })),
  );

  const goalScore = weightedMean(
    input.goalLinks.map((g) => ({
      weight: new Decimal(g.weightPercent),
      // #19: over-achievement is capped — 150% of a goal scores as 100%, never above the scale.
      value: capAchievement(g.managerAdjustedPercent ?? g.achievedPercent),
    })),
  );

  // Rescale goalScore (0-100) onto the same 1..ratingScaleMax axis competencyScore already lives
  // on — see this file's header "UNITS" note for why blending would otherwise be wrong by ~20x.
  const goalScoreOnRatingScale = goalScore.div(100).times(ratingScaleMax);

  const goalWeight = new Decimal(input.goalWeightPercent);
  const competencyWeight = new Decimal(input.competencyWeightPercent);
  const totalConfiguredWeight = goalWeight.plus(competencyWeight);

  const overallScore = totalConfiguredWeight.isZero()
    ? new Decimal(0)
    : competencyScore
        .times(competencyWeight)
        .plus(goalScoreOnRatingScale.times(goalWeight))
        .div(totalConfiguredWeight);

  const bands =
    input.ratingBands && input.ratingBands.length > 0
      ? input.ratingBands
      : defaultBands(ratingScaleMax);

  const hasAnyInput = input.ratings.length > 0 || input.goalLinks.length > 0;

  return {
    competencyScore: competencyScore.toFixed(2),
    goalScore: goalScore.toFixed(2),
    overallScore: overallScore.toFixed(2),
    ratingLabel: hasAnyInput ? labelFor(overallScore, bands) : null,
  };
}

// ---------------------------------------------------------------------------
// The manager-led score (D1, remediation plan §5C, ADR 0031 §2)
// ---------------------------------------------------------------------------
//
// THE ONE AUTHORITATIVE SCORE. `computeReviewScore` above blends whatever ratings it is handed and
// scores an empty half as "0.00", which drags a review with no goals down to half the scale. The
// manager-led model differs in exactly three ways, each an acceptance criterion:
//
//   1. INPUT SELECTION is done here, not by the caller: competencies score the MANAGER's rating,
//      goals score `min(100, managerAdjustedPercent ?? achievedPercent)`. Self, peer and upward
//      ratings are shown as input and never scored.
//   2. WEIGHT TRANSFER: a half with nothing to score (no rows, or every weight 0) is not a zero —
//      its configured weight moves to the other half. No goals + every manager rating 5 → 5.00.
//      Both halves empty → `nothing_to_score`; the caller refuses (412) and never persists "0.00".
//   3. COMPLETENESS: every competency passed in must carry a manager rating; any that does not is
//      named back (`missing_manager_ratings`) instead of being silently left out of the mean.
//
// The same blend, over self ratings and the employee's own achievement, produces the display-only
// `selfOverallScore` (`computeSelfScore`), so the two numbers a reader compares side by side are
// computed the same way.

/** One template competency, as the manager-led scorer sees it. */
export interface ManagerLedRatingInput {
  /** Echoed back in `missing_manager_ratings`; optional for callers that only need the maths. */
  competencyId?: string;
  /** The weight snapshotted on the rating row (or the template's, when no row exists yet). */
  weightPercent: string;
  /** `ReviewCompetencyRating.managerRating`; `null` when the manager has not rated it. */
  managerRating: string | null;
}

export interface ManagerLedGoalInput {
  weightPercent: string;
  achievedPercent: string | null;
  managerAdjustedPercent?: string | null;
}

export interface ScoreBlendConfig {
  goalWeightPercent: string;
  competencyWeightPercent: string;
  /** Defaults to 5 — `ReviewCycle.ratingScaleMax`. */
  ratingScaleMax?: number;
  ratingBands?: RatingBand[];
}

export interface ComputeManagerLedScoreInput extends ScoreBlendConfig {
  ratings: ManagerLedRatingInput[];
  goalLinks: ManagerLedGoalInput[];
}

export interface BlendedScore {
  /** Weighted mean on the 1..ratingScaleMax scale; `null` when the competency half was empty. */
  competencyScore: string | null;
  /** Weighted mean of capped achievement, 0-100; `null` when the goal half was empty. */
  goalScore: string | null;
  /** The blend on the rating scale, after any weight transfer. */
  overallScore: string;
  ratingLabel: string | null;
  /** The weights actually applied (after transfer), as percentages summing to 100. */
  appliedGoalWeightPercent: string;
  appliedCompetencyWeightPercent: string;
}

export type ManagerLedScoreResult =
  | ({ status: 'scored' } & BlendedScore)
  /** At least one competency lacks a manager rating — the caller answers 412 naming them. */
  | { status: 'missing_manager_ratings'; competencyIds: string[]; missingCount: number }
  /** No competency and no goal carries any weight — there is nothing to score (412). */
  | { status: 'nothing_to_score' };

/** A half's weighted mean, or `null` when it has nothing to score (no rows, or zero total weight). */
function halfMean(values: Array<{ weight: Decimal; value: Decimal }>): Decimal | null {
  const totalWeight = values.reduce((sum, v) => sum.plus(v.weight), new Decimal(0));
  if (values.length === 0 || totalWeight.isZero()) return null;
  return weightedMean(values);
}

/** Blend the two halves with weight transfer. `null` when both halves are empty. */
function blendWithTransfer(
  competencyMean: Decimal | null,
  goalMean: Decimal | null,
  config: ScoreBlendConfig,
): BlendedScore | null {
  if (competencyMean === null && goalMean === null) return null;
  const ratingScaleMax = config.ratingScaleMax ?? DEFAULT_RATING_SCALE_MAX;

  let goalWeight = new Decimal(config.goalWeightPercent);
  let competencyWeight = new Decimal(config.competencyWeightPercent);
  if (competencyMean === null) {
    // Weight transfer: the empty half's configured weight moves to the other half.
    goalWeight = goalWeight.plus(competencyWeight);
    competencyWeight = new Decimal(0);
  } else if (goalMean === null) {
    competencyWeight = competencyWeight.plus(goalWeight);
    goalWeight = new Decimal(0);
  }

  let total = goalWeight.plus(competencyWeight);
  if (total.isZero()) {
    // Only reachable with a cycle configured 0/0, which cycle validation refuses (weights must sum
    // to 100). Blend the present halves equally rather than divide by zero.
    goalWeight = goalMean === null ? new Decimal(0) : new Decimal(50);
    competencyWeight = competencyMean === null ? new Decimal(0) : new Decimal(50);
    total = goalWeight.plus(competencyWeight);
  }

  // Goal achievement (0-100) is rescaled onto the rating axis before blending — see "UNITS" above.
  const goalOnRatingScale = goalMean?.div(100).times(ratingScaleMax) ?? new Decimal(0);
  const overall = (competencyMean ?? new Decimal(0))
    .times(competencyWeight)
    .plus(goalOnRatingScale.times(goalWeight))
    .div(total);

  const bands =
    config.ratingBands && config.ratingBands.length > 0
      ? config.ratingBands
      : defaultBands(ratingScaleMax);

  return {
    competencyScore: competencyMean?.toFixed(2) ?? null,
    goalScore: goalMean?.toFixed(2) ?? null,
    overallScore: overall.toFixed(2),
    ratingLabel: labelFor(overall, bands),
    appliedGoalWeightPercent: goalWeight.div(total).times(100).toFixed(2),
    appliedCompetencyWeightPercent: competencyWeight.div(total).times(100).toFixed(2),
  };
}

function goalHalfMean(goalLinks: ManagerLedGoalInput[]): Decimal | null {
  return halfMean(
    goalLinks.map((g) => ({
      weight: new Decimal(g.weightPercent),
      value: capAchievement(g.managerAdjustedPercent ?? g.achievedPercent),
    })),
  );
}

/**
 * The authoritative review score (D1): manager ratings + capped, manager-adjusted goal achievement,
 * with weight transfer between halves. Pure; Decimal throughout.
 */
export function computeManagerLedScore(input: ComputeManagerLedScoreInput): ManagerLedScoreResult {
  const missing = input.ratings.filter((r) => r.managerRating === null);
  if (missing.length > 0) {
    return {
      status: 'missing_manager_ratings',
      competencyIds: missing.flatMap((r) => (r.competencyId ? [r.competencyId] : [])),
      missingCount: missing.length,
    };
  }

  const competencyMean = halfMean(
    input.ratings.map((r) => ({
      weight: new Decimal(r.weightPercent),
      value: new Decimal(r.managerRating as string),
    })),
  );
  const blended = blendWithTransfer(competencyMean, goalHalfMean(input.goalLinks), input);
  return blended ? { status: 'scored', ...blended } : { status: 'nothing_to_score' };
}

export interface ComputeSelfScoreInput extends ScoreBlendConfig {
  /** Unrated competencies (`selfRating: null`) are left out of the mean, not scored as zero. */
  ratings: Array<{ weightPercent: string; selfRating: string | null }>;
  /** The employee's own achievement; a manager adjustment is not the employee's view. */
  goalLinks: Array<{ weightPercent: string; achievedPercent: string | null }>;
}

/**
 * The employee's own score (`selfOverallScore`) — DISPLAY ONLY, never the authoritative score
 * (D1). Same blend and weight transfer as the manager-led score so the two compare like for like.
 * `null` when the employee rated nothing and linked no goal.
 */
export function computeSelfScore(input: ComputeSelfScoreInput): BlendedScore | null {
  const competencyMean = halfMean(
    input.ratings.flatMap((r) =>
      r.selfRating === null
        ? []
        : [{ weight: new Decimal(r.weightPercent), value: new Decimal(r.selfRating) }],
    ),
  );
  const goalMean = goalHalfMean(
    input.goalLinks.map((g) => ({ weightPercent: g.weightPercent, achievedPercent: g.achievedPercent })),
  );
  return blendWithTransfer(competencyMean, goalMean, input);
}

/** A Prisma `Decimal`, a decimal string, or nothing. */
type DecimalLike = string | { toString(): string } | null | undefined;

/**
 * `effectiveScore(review) = finalRating ?? overallScore` — the ONE helper behind calibration,
 * rollups, merit, insights, export, the PDF and the dashboard (plan §5C). `finalRating` is the
 * calibrated value; without one, the manager-led `overallScore`. Decimal string in, decimal string
 * out (never a float); `null` when the review has neither.
 */
export function effectiveScore(review: {
  finalRating?: DecimalLike;
  overallScore?: DecimalLike;
}): string | null {
  const value = review.finalRating ?? review.overallScore;
  if (value === null || value === undefined) return null;
  const text = value.toString();
  // Throws on a non-decimal value rather than passing garbage downstream; keeps the stored precision.
  new Decimal(text);
  return text;
}
